
import {
  Component, Ref, Watch, Prop, Mixins, PropSync, Inject,
} from 'vue-property-decorator';
import { VForm } from '@/features/common/types';
import {
  InProgress,
  PackageItem,
  SignatureType,
} from '@/features/shipment/types';
import PackageSummary from '@/features/shipment/components/createShipment/packageSummary/PackageSummary.vue';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { UserPreferences } from '@/features/settings/types';
import {
  scrollByInPage, scrollToBottom, showErrorAlertDialog,
} from '@/features/common/helpers';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { EstimateDataType } from '@/features/estimate/types';
import { CreateShipmentValidationState } from '@/features/shipment/helpers/createShipmentValidation';
import AddPackageInputs, { FormData } from './AddPackageInputs.vue';
import PackageSummaryMobile from './mobileComponents/PackageSummaryMobile.vue';
import EditPackageItem from './EditPackageItem.vue';
import { FormValidationType } from './ExtrasShipment.vue';

@Component({
  components: {
    PackageSummary,
    PackageSummaryMobile,
    EditPackageItem,
    AddPackageInputs,
  },
})
export default class WhatShipmentForm extends Mixins(ResponsivenessMixin) {
  @Inject({
    from: 'createShipmentValidationState',
    default: () => ({ attempted: true }),
  })
  private createShipmentValidationState!: CreateShipmentValidationState;

  @Ref('formWhatShipment') formWhatShipment!: VForm;

  // this flag will determine whether to create shipment or just estimate
  @Prop({ required: false, default: false }) isJustEstimate!: boolean;

  @PropSync('validationMessage', {
    default: () => ({
      isValid: true,
      message: '',
      isHint: true,
    }),
  }) formValidationMsg!: FormValidationType;

  private packageData: FormData = {
    packageType: 'MyPackage',
    weightUnits: 'Lbs',
    weight: '',
    dimUnits: '',
    length: '',
    height: '',
    width: '',
    insurance: '',
    description: '',
    isAdditionalHandling: false,
  }

  private packageSummary: Array<PackageItem> = [];

  private isShowEditPackage = false;

  private packageItemToEdit: PackageItem & { index?: number } = this.packageSummary[0];

  private isFormChanged = false;

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get estimateData(): EstimateDataType {
    return this.$store.state.estimate.estimateData;
  }

  get isAutomaticallyAddPackage(): boolean {
    if (!this.userPreferences.shippingSettings.isAutomaticallyAddPackage) return false;
    if (this.packageData.packageType === 'Envelope' && this.packageSummary.length) return false;

    return true;
  }

  get totalPackages(): number {
    return this.packageSummary.length;
  }

  @Watch('isUserPreferences', { immediate: true })
  private setUserPrefsData(newValue: number): void {
    if (!newValue) return;

    const {
      packageType,
      weightUnits,
      dimUnits,
    } = this.userPreferences.shippingSettings;

    this.packageData.packageType = packageType;
    this.packageData.weightUnits = weightUnits;
    this.packageData.dimUnits = dimUnits;
    this.packageData.weight = '';
    this.packageData.length = '';
    this.packageData.width = '';
    this.packageData.height = '';
    this.packageData.insurance = '';
    this.packageData.description = '';
    this.packageData.isAdditionalHandling = false;

    const { temporaryPackage } = this.inProgressData;

    // only update to store if there is no in-progress data and not just estimate
    if (!this.isJustEstimate && (!temporaryPackage || Object.values(temporaryPackage).every(
      (x) => ['MyPackage', 'None', ''].includes(x) || x === false || x === 0,
    ))) {
      this.$store.commit('patchInProgress', {
        key: 'temporaryPackage',
        value: {
          packageType: packageType ?? 'MyPackage',
          weight: '',
          weightUnits: weightUnits ?? 'Lbs',
          length: '',
          width: '',
          height: '',
          dimUnits: dimUnits ?? 'Inches',
          insurance: '',
          isAdditionalHandling: false,
          description: '',
        },
      });
    }

    if (this.isJustEstimate) {
      const { packages } = this.estimateData;
      if (packages.length) {
        this.packageSummary = packages;
      }
    }
  }

  @Watch('isInProgressData', { immediate: true })
  private replaceData(newValue: number) {
    if (!newValue) return;
    if (this.isJustEstimate) return;

    const { packages } = this.inProgressData;

    if (packages?.length) {
      this.packageSummary = packages;
    }

    if (this.inProgressData.temporaryPackage) {
      const {
        weight, length, width, height, insurance, weightUnits, dimUnits,
      } = this.inProgressData.temporaryPackage;

      this.packageData = {
        ...this.inProgressData.temporaryPackage,
        weightUnits: weightUnits || 'Lbs',
        dimUnits: dimUnits || 'Inches',
        weight: weight || '',
        width: width || '',
        height: height || '',
        length: length || '',
        insurance: insurance || '',
      };
    }
  }

  mounted(): void {
    this.$store.commit('setWhatShipmentForm', this.$refs.formWhatShipment);
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
    eventBus.$on(eventNames.CLEAR_ESTIMATE_FORM, this.clearAllInformation);
    eventBus.$on(eventNames.SUBMIT_WHAT_SHIPMENT, this.submitWhatShipmentForm);
    eventBus.$on(eventNames.UPDATE_PACKAGES_SIGNATURE, this.updatePackagesSignature);
  }

  destroyed(): void {
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
    eventBus.$off(eventNames.CLEAR_ESTIMATE_FORM, this.clearAllInformation);
    eventBus.$off(eventNames.SUBMIT_WHAT_SHIPMENT);
    eventBus.$off(eventNames.UPDATE_PACKAGES_SIGNATURE);
  }

  private getSignatureOption(): keyof typeof SignatureType {
    if (this.isJustEstimate) {
      return this.estimateData.signatureOptions || 'None';
    }

    const { signatureOptions } = this.inProgressData;
    const { signatureOption: signatureFromPref } = this.userPreferences.shippingSettings;
    return signatureOptions || signatureFromPref;
  }

  private async addPackage(): Promise<boolean> {
    if (this.packageSummary.length >= 20) {
      showErrorAlertDialog(this.$t('shipment.packages20Restriction') as string);
      return false;
    }

    let weight = +this.packageData.weight;
    let { weightUnits } = this.packageData;

    if (this.packageData.packageType === 'Envelope') {
      weight = 1;
      weightUnits = 'Lbs';
    }

    const data: PackageItem = {
      ...this.packageData,
      weightUnits,
      weight,
      length: +this.packageData.length,
      width: +this.packageData.width,
      height: +this.packageData.height,
      insurance: +this.packageData.insurance,
      signatureOptions: this.getSignatureOption(),
    };

    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();

    const isValid = this.formWhatShipment.validate();
    if (isValid) {
      const packages = [
        ...this.packageSummary,
        data,
      ];

      this.packageSummary = packages;

      this.clearData(
        this.packageData.packageType === 'Pallet',
      );

      this.storePackagesData();

      scrollByInPage(0, 55, true);
    }

    return isValid;
  }

  private duplicatePackage(index?: number): void {
    if (this.packageSummary.length >= 20) {
      showErrorAlertDialog(this.$t('shipment.packages20Restriction') as string);
      return;
    }

    const targetIndex = typeof index === 'number' ? index : this.packageSummary.length - 1;

    const originalPackage = this.packageSummary[targetIndex];
    const duplicatedPackage = { ...originalPackage };

    this.packageSummary.splice(targetIndex + 1, 0, duplicatedPackage);
    this.storePackagesData();
  }

  private deletePackage(idx: number): void {
    eventBus.$once(eventNames.CONFIRMATION_DIALOG, () => {
      this.packageSummary.splice(idx, 1);

      this.storePackagesData();
    });

    this.$store.commit('toggleConfirmDialog', {
      show: true,
    });
  }

  private openEditPackageModal(idx: number): void {
    this.packageItemToEdit = {
      ...this.packageSummary[idx],
      index: idx,
    };
    this.isShowEditPackage = true;
  }

  private updatePackage(data: PackageItem, index: number) {
    if (index || index === 0) {
      this.$set(this.packageSummary, index, data);
      this.storePackagesData();
    }
  }

  private storePackagesData() {
    if (this.isJustEstimate) {
      this.$store.commit('patchEstimateData', {
        key: 'packages',
        value: this.packageSummary,
      });
      return;
    }

    this.$store.dispatch('saveInProgressData', {
      key: 'packages',
      value: this.packageSummary,
    });
  }

  private updatePackagesSignature(signatureOptions: keyof typeof SignatureType) {
    this.packageSummary = this.packageSummary.map((packageItem) => ({
      ...packageItem,
      signatureOptions,
    }));
  }

  private clearData(isFreightPackage = false): void {
    const {
      packageType,
      weightUnits,
      dimUnits,
    } = this.userPreferences.shippingSettings;

    this.packageData = {
      packageType: isFreightPackage ? 'Pallet' : packageType ?? 'MyPackage',
      weightUnits,
      weight: '',
      dimUnits,
      length: '',
      height: '',
      width: '',
      insurance: '',
      description: '',
      isAdditionalHandling: false,
    };

    this.resetValidation();
    this.updateInProgressTempPackageData(true, false);

    this.showValidationMessage({
      isValid: true,
      message: '',
      isHint: true,
    });
  }

  private async submitWhatShipmentForm() {
    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();

    const isFormValid = this.formWhatShipment.validate();

    if (this.isAutomaticallyAddPackage && isFormValid) {
      await this.addPackage();
    }

    const isPackages = this.packageSummary.length;

    if (!isPackages) {
      this.showValidationMessage({
        isValid: false,
        message: this.$t('shipment.noPackageWarning') as string,
        isHint: false,
      });
      scrollToBottom();
      this.$emit('submit', false);
      return;
    }

    if (this.isTempPackageData()) {
      this.validateForm();
      this.$store.commit('toggleConfirmDialog', {
        show: true,
        btnText: this.$t('buttons.ok') as string,
        content: this.$t('shipment.existingTempPackageWarning') as string,
        title: this.$t('alertDialog.areYouSure'),
      });
      eventBus.$once(eventNames.CONFIRMATION_DIALOG, () => {
        this.clearData();
        this.resetValidation();
        this.$emit('submit', true);
      });
      return;
    }

    this.resetValidation();
    this.$emit('submit', true);
  }

  private resetValidation() {
    this.createShipmentValidationState.attempted = false;
    (this.$refs.formWhatShipment as HTMLFormElement).resetValidation();
  }

  private isTempPackageData() {
    const {
      packageType,
      weight,
      length,
      width,
      height,
      insurance,
      isAdditionalHandling,
      // description,
    } = this.packageData;

    // if (description) return true;
    if (packageType === 'Envelope') return false;
    if (weight || insurance || isAdditionalHandling) return true;
    if (length || width || height) return true;

    return false;
  }

  private updateInProgressTempPackageData(force = false, sync = true) {
    this.parseNumberToAbsoluteValue();

    if (!force && !this.isFormChanged) return;
    if (this.isJustEstimate) return;

    const {
      packageType,
      weight,
      weightUnits,
      length,
      width,
      height,
      dimUnits,
      insurance,
      isAdditionalHandling,
      description,
    } = this.packageData;

    this.$store.commit('patchInProgress', {
      key: 'temporaryPackage',
      value: {
        packageType: packageType ?? 'MyPackage',
        weight,
        weightUnits: weightUnits ?? 'Lbs',
        length,
        width,
        height,
        dimUnits: dimUnits ?? 'Inches',
        insurance,
        isAdditionalHandling: isAdditionalHandling ?? false,
        signatureOptions: this.getSignatureOption(),
        description: description ?? '',
      },
    });

    if (!sync) return;
    this.$store.dispatch('syncInProgressData');
  }

  private parseNumberToAbsoluteValue() {
    for (const key in this.packageData) {
      if (Object.prototype.hasOwnProperty.call(this.packageData, key)) {
        const value = this.packageData[key];
        if (value && !Number.isNaN(parseInt(value, 10))) {
          this.packageData[key] = Math.abs(value);
        }
      }
    }
  }

  private clearAllInformation() {
    this.packageSummary = [];
    this.resetValidation();

    if (this.isJustEstimate) {
      this.clearData();
    }

    this.isFormChanged = false;
  }

  private validateForm(): void {
    if (this.$refs.formWhatShipment) {
      (this.$refs.formWhatShipment as HTMLFormElement).validate();
    }
  }

  private showValidationMessage(payload: FormValidationType) {
    this.formValidationMsg = payload;
  }
}
